<script setup lang="ts">
import type { ShortcutSearchHit } from "./types";
import type { FilesystemJs, SearchFolderRowJs } from "@/generated/models";
import { CommandEmpty, CommandGroup, CommandLoading } from "ls/components/command";
import { useCommandContext } from "ls/components/command/context";
import { toWorkspace } from "ls/router/builders";
import { FolderIcon } from "lucide-vue-next";
import { computed } from "vue";
import { useRouter } from "vue-router";
import FileIcon from "../files/FileIcon.vue";
import WorkspaceIcon from "../workspaces/WorkspaceIcon.vue";
import CommandShortcutEnter from "./CommandShortcutEnter.vue";
import SearchItem from "./SearchItem.vue";
import { getFileHref } from "./searchLinks";
import SearchSkeleton from "./SearchSkeleton.vue";
import { useCombinedSearch } from "./useCombinedSearch";

const emit = defineEmits<{
  select: [];
}>();

const { searchTerm } = useCommandContext();
const searchVal = computed(() => searchTerm.value ?? "");
const {
  workspaces,
  folders,
  filesAndDeep,
  isPending,
  isLoading,
} = useCombinedSearch(searchVal, { limit: 10 });

const noResults = computed(() => {
  const totalWorkspaces = workspaces.value?.length;
  const totalFolders = folders.value?.length;
  const totalFiles = filesAndDeep.value?.length;
  return totalWorkspaces === 0 && totalFolders === 0 && totalFiles === 0;
});

const router = useRouter();
async function goToFile(file: ShortcutSearchHit) {
  emit("select");
  await router.push(getFileHref(file));
}

async function goToWorkspace(workspace: FilesystemJs) {
  emit("select");
  await router.push(toWorkspace(workspace));
}

async function goToFolder(folder: SearchFolderRowJs) {
  emit("select");
  await router.push(toWorkspace(folder.FilesystemId, { folderId: folder.Folder.FolderId }));
}
</script>

<template>
  <CommandLoading v-if="isPending" />
  <CommandGroup v-if="!isLoading && filesAndDeep.length" heading="Files and content">
    <SearchItem
      v-for="file in filesAndDeep"
      :key="file.id"
      :value="file"
      :tagged-title="file.title"
      :path="file.path"
      :tagged-highlight="file.detail"
      :href="getFileHref(file)"
      @select="goToFile(file)"
    >
      <template #icon>
        <FileIcon class="size-4 shrink-0" :file="file.metadata" />
      </template>
      <CommandShortcutEnter />
    </SearchItem>
  </CommandGroup>
  <CommandGroup v-if="!isLoading && (workspaces.length || folders.length)" heading="Workspaces and folders">
    <SearchItem
      v-for="workspace in workspaces"
      :key="workspace.id"
      :value="workspace"
      :tagged-title="workspace.title"
      :path="workspace.path"
      :tagged-highlight="workspace.detail"
      :href="toWorkspace(workspace.metadata)"
      @select="goToWorkspace(workspace.metadata)"
    >
      <template #icon>
        <WorkspaceIcon
          class="shrink-0"
          aria-hidden="true"
          :workspace="workspace.metadata"
        />
      </template>
      <CommandShortcutEnter />
    </SearchItem>
    <SearchItem
      v-for="folder in folders"
      :key="folder.id"
      :value="folder"
      :tagged-title="folder.title"
      :path="folder.path"
      :tagged-highlight="folder.detail"
      :href="toWorkspace(folder.metadata.FilesystemId, { folderId: folder.metadata.Folder.FolderId })"
      @select="goToFolder(folder.metadata)"
    >
      <template #icon>
        <FolderIcon
          class="size-4 shrink-0"
          aria-hidden="true"
        />
      </template>
      <CommandShortcutEnter />
    </SearchItem>
  </CommandGroup>

  <SearchSkeleton
    v-if="isLoading"
    :count="10"
  />

  <CommandEmpty v-if="!isLoading && noResults">
    Nothing to show here
  </CommandEmpty>
</template>
